/**
 * TypeScript interfaces for AuditSessionsFilters component
 *
 * Defines the data structures for filtering and sorting audit sessions
 * with support for multiple filter categories and sort options.
 */

import type { AuditSessionStatus } from '@internal-audit/domain';
import type { MultiSelectOption, DropdownOption } from '@soteria/design-system-ve';

/**
 * Page size options for audit sessions pagination
 * Limited to API maximum of 100
 */
export type AuditSessionPageSize = 5 | 25 | 50 | 100;

/**
 * Available sort fields for audit sessions
 */
export type AuditSessionSortField = 'createdAt' | 'completedAt' | 'itemsCount';

/**
 * Sort direction options
 */
export type SortDirection = 'asc' | 'desc';

/**
 * Comprehensive filter state for audit sessions
 */
export interface AuditSessionFilters {
  /** Status filter - multiple statuses can be selected */
  status: AuditSessionStatus[];
  /** Label filter - multiple labels can be selected */
  labels: string[];
  /** Topic filter - multiple topics can be selected */
  topics: string[];
  /** Language filter - multiple languages can be selected */
  languages: string[];
}

/**
 * Sort configuration
 */
export interface AuditSessionSort {
  field: AuditSessionSortField;
  direction: SortDirection;
}

/**
 * Available filter options passed from parent component
 */
export interface FilterOptions {
  /** Available labels for selection */
  availableLabels: string[];
  /** Available topics for selection */
  availableTopics: string[];
  /** Available languages for selection */
  availableLanguages: string[];
}

/**
 * Props for the main AuditSessionsFilters component
 */
export interface AuditSessionsFiltersProps {
  /** Current filter state */
  filters: AuditSessionFilters;
  /** Callback when filters change */
  onFiltersChange: (filters: AuditSessionFilters) => void;

  /** Current sort configuration */
  sort: AuditSessionSort;
  /** Callback when sort changes */
  onSortChange: (sort: AuditSessionSort) => void;

  /** Current page size */
  pageSize: AuditSessionPageSize;
  /** Callback when page size changes */
  onPageSizeChange: (pageSize: AuditSessionPageSize) => void;

  /** Available options for filter dropdowns */
  filterOptions: FilterOptions;

  /** Loading state to disable interactions */
  isLoading?: boolean;
}

/**
 * Internal state for UI interactions
 */
export interface FilterUIState {
  /** Whether the filter card is expanded */
  isFilterExpanded: boolean;
}

/**
 * Helper type for creating empty filter state
 */
export const createEmptyFilters = (): AuditSessionFilters => ({
  status: [],
  labels: [],
  topics: [],
  languages: [],
});

/**
 * Helper type for creating default sort state
 */
export const createDefaultSort = (): AuditSessionSort => ({
  field: 'createdAt',
  direction: 'desc',
});

/**
 * Sort field options for dropdown
 */
export const SORT_FIELD_OPTIONS: DropdownOption[] = [
  { id: 'createdAt', label: 'Created Date' },
  { id: 'completedAt', label: 'Completed Date' },
  { id: 'itemsCount', label: 'Items Count' },
];

/**
 * Status filter options including 'All' option
 */
export const STATUS_FILTER_OPTIONS: MultiSelectOption[] = [
  { id: 'all', value: 'all', label: 'All Statuses' },
  { id: 'active', value: 'active', label: 'Active' },
  { id: 'completed', value: 'completed', label: 'Completed' },
  { id: 'expired', value: 'expired', label: 'Expired' },
];

/**
 * Page size options for audit sessions dropdown
 * Limited to API maximum of 100
 */
export const AUDIT_SESSION_PAGE_SIZE_OPTIONS: DropdownOption[] = [
  { id: '5', label: '5 per page' },
  { id: '25', label: '25 per page' },
  { id: '50', label: '50 per page' },
  { id: '100', label: '100 per page' },
];
